import { useEffect, useState, type ChangeEvent } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AclSettingsModal,
  AclModalSection,
  AclModalOptionCheck,
  FaUsers,
} from '@/features/acl-settings/aclSettingsUi';
import {
  CONTACT_CUSTOM_FIELDS_ACL_OPTION,
  normalizeContactsEditOptions,
} from '@/utilities/contactCustomFieldsAcl';

interface ContactsCustomFieldsAclModalProps {
  show: boolean;
  onHide: () => void;
  initialOptions?: unknown;
  canGrant: boolean;
  limitedByParent?: boolean;
  /** Users on this ACL (or under them) already using custom fields; the option is locked on. */
  usersInUse?: string[];
  onSave: (value: { edit_options: string[] }) => void;
}

/** "N users" with a View link that opens the full, scrollable list. */
function UsersInUse({ users }: { users: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <strong>
        {users.length} {users.length === 1 ? 'user' : 'users'}
      </strong>{' '}
      <button
        type="button"
        className="font-medium underline underline-offset-2 hover:opacity-80"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide' : 'View'}
      </button>
      {open && (
        <ul className="mt-2 max-h-40 list-disc overflow-y-auto ps-5">
          {users.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Contacts → Options. Unchecked by default. */
export default function ContactsCustomFieldsAclModal({
  show,
  onHide,
  initialOptions,
  canGrant,
  limitedByParent = false,
  usersInUse = [],
  onSave,
}: ContactsCustomFieldsAclModalProps) {
  const [enabled, setEnabled] = useState(false);
  const savedOn = normalizeContactsEditOptions(initialOptions).includes(CONTACT_CUSTOM_FIELDS_ACL_OPTION);
  const locked = savedOn && usersInUse.length > 0;

  useEffect(() => {
    if (!show) return;
    setEnabled(canGrant && savedOn);
  }, [show, savedOn, canGrant]);

  const handleSave = () => {
    onSave({ edit_options: (enabled || locked) && canGrant ? [CONTACT_CUSTOM_FIELDS_ACL_OPTION] : [] });
    onHide();
  };

  return (
    <AclSettingsModal
      show={show}
      onHide={onHide}
      title="Contacts — Options"
      icon={FaUsers}
      description="Configure which contact capabilities users with this ACL can use."
      limitedByParent={limitedByParent}
      onSave={handleSave}
      onReset={() => setEnabled(locked)}
      saveDisabled={!canGrant}
    >
      <AclModalSection
        icon={FaUsers}
        title="Custom fields"
        subtitle="Unchecked by default. Lets users build custom field templates and use them on contacts."
      >
        {!canGrant ? (
          <p className="acl-modal-empty">You do not have permission to grant contact custom fields.</p>
        ) : (
          <>
            <AclModalOptionCheck
              id="contacts-acl-custom-fields"
              label="Custom fields"
              description="Create custom field templates and pick one while creating, editing or uploading contacts"
              checked={enabled || locked}
              disabled={locked}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEnabled(e.target.checked)}
            />
            {locked && (
              <div className="mt-2 mb-0 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
                Custom fields are in use, so this option is locked and can't be turned off. Used by{' '}
                <UsersInUse users={usersInUse} />
              </div>
            )}
          </>
        )}
      </AclModalSection>
    </AclSettingsModal>
  );
}

/** Popup shown when an edit would turn off Custom fields that are in use. */
export function CustomFieldsInUseDialog({
  users,
  open,
  onOpenChange,
}: {
  users: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Custom fields can't be turned off</DialogTitle>
          <DialogDescription>
            Keep Contacts (Read, Add or Edit) and the Custom fields option on for this ACL.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm">
          Contact custom fields are already used by <UsersInUse users={users} />
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>OK</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
